import { ComboboxButton, ComboboxOption, ComboboxOptions, Field } from '@headlessui/react'
import {
  ArrowButton,
  CloseButton,
  ComboboxSinge,
  FieldErrorAndDescription,
  FieldLabel,
  FormSelectDataType,
  FormSelectIdType,
  FormSelectInput,
  FormSelectProps,
  getSelectedData,
  useFilteredData,
} from '@systemeio/ui'
import { useEffect, useState } from 'react'
import { ListOptionsWithGroups } from 'shared/components/form-select/components/list-options-with-groups'
import { twMerge } from 'tailwind-merge'

export interface FormSelectDataWithGroupType<T extends FormSelectIdType = FormSelectIdType>
  extends FormSelectDataType<T> {
  group: string
}

interface FormSelectWithGroupsProps<T extends FormSelectIdType>
  extends Omit<FormSelectProps<T>, 'data'> {
  groups: { id: string; caption: string }[]
  data: FormSelectDataWithGroupType<T>[]
}
export function FormSelectWithGroups<T extends FormSelectIdType>({
  label,
  data,
  staticData,
  onChange,
  labelClassName,
  value,
  isPreFetching,
  placeholder,
  error,
  required,
  description,
  className,
  containerClassName,
  closeButtonClassName,
  arrowButtonClassName,
  fieldClassName,
  disabled,
  withoutCloseIcon,
  onOpen,
  onClose,
  dataTest,
  wrapperProps,
  loaderClassName,
  loaderWrapperClassName,
  inputWrapperClassName,
  inputRef,
  groups,
  ...optionsProps
}: FormSelectWithGroupsProps<T>) {
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    if (opened && onOpen) {
      onOpen()
      setOpened(false)
    }
  }, [opened])

  const selectedData = getSelectedData(value, data ?? staticData) ?? null

  const { filteredData, setQuery } = useFilteredData(data)

  const hasValue = value !== undefined

  const noData = !data && !staticData

  const onlyValue = hasValue && noData

  const isDisabled = disabled || isPreFetching || onlyValue

  const showCloseButton = hasValue && !withoutCloseIcon && !isDisabled

  return (
    <div className={twMerge('relative flex flex-col gap-1', containerClassName)}>
      <ComboboxSinge
        selectedData={selectedData}
        onChange={onChange}
        disabled={isDisabled}
        onClose={() => {
          setQuery('')
          onClose?.()
        }}
      >
        {open => (
          <>
            <Field className={twMerge('relative flex flex-col gap-1', fieldClassName)}>
              <FieldLabel
                wrapperAs={'label'}
                wrapperProps={wrapperProps}
                label={label}
                required={required}
                labelClassName={twMerge('text-sm font-medium', labelClassName)}
              />
              <ComboboxButton
                as="button"
                className={twMerge('relative', inputWrapperClassName)}
              >
                <FormSelectInput
                  dataTest={dataTest}
                  inputRef={inputRef}
                  isDisabled={isDisabled}
                  hasError={!!error}
                  open={open}
                  className={className}
                  placeholder={placeholder}
                  selectedCaption={selectedData?.caption ?? ''}
                  isPreFetching={isPreFetching}
                  onFocus={() => setOpened(true)}
                  onChange={value => {
                    setOpened(true)
                    setQuery(value)
                  }}
                />
                <ArrowButton
                  isDisabled={isDisabled}
                  arrowButtonClassName={twMerge(
                    'absolute inset-y-0 right-4 h-[46px] sm:h-[42px]',
                    arrowButtonClassName,
                  )}
                  open={open}
                />
              </ComboboxButton>
              {showCloseButton && (
                <CloseButton
                  isDisabled={isDisabled}
                  closeButtonClassName={twMerge(
                    'absolute bottom-0 right-10 h-[46px] sm:h-[42px]',
                    closeButtonClassName,
                  )}
                  onClick={() => onChange(undefined)}
                />
              )}
            </Field>
            <ListOptionsWithGroups
              asOptions={ComboboxOptions}
              asOption={ComboboxOption}
              data={filteredData}
              groups={groups}
              {...optionsProps}
            />
          </>
        )}
      </ComboboxSinge>
      <FieldErrorAndDescription error={error} description={description} />
    </div>
  )
}
